// Bounding boxes for the coverage overlay on the tracking map.
// A box is { south, west, north, east } in degrees. Every function returns a
// new box and leaves its input alone.

const PAD_FACTOR = 0.5;
const SNAP_DEGREES = 0.25;
const MAX_LAT = 90;
const MAX_LNG = 180;

function clamp(value, limit) {
    return Math.max(-limit, Math.min(limit, value));
}

// Leaflet reports longitudes beyond 180 when the map is zoomed far out or
// dragged around the world.
export function clampBox(box) {
    return {
        south: clamp(box.south, MAX_LAT),
        west: clamp(box.west, MAX_LNG),
        north: clamp(box.north, MAX_LAT),
        east: clamp(box.east, MAX_LNG),
    };
}

export function isValidBox(box) {
    if (!box) {
        return false;
    }
    const sides = [box.south, box.west, box.north, box.east];
    if (!sides.every(side => Number.isFinite(side))) {
        return false;
    }
    return box.south < box.north && box.west < box.east;
}

export function padBox(box, factor = PAD_FACTOR) {
    const latPad = (box.north - box.south) * factor;
    const lngPad = (box.east - box.west) * factor;
    return {
        south: box.south - latPad,
        west: box.west - lngPad,
        north: box.north + latPad,
        east: box.east + lngPad,
    };
}

// Widening to a coarse grid makes viewers of the same race ask for the same
// box, so the cached response is reused.
export function snapBox(box, step = SNAP_DEGREES) {
    return {
        south: Math.floor(box.south / step) * step,
        west: Math.floor(box.west / step) * step,
        north: Math.ceil(box.north / step) * step,
        east: Math.ceil(box.east / step) * step,
    };
}

export function requestBox(viewBox) {
    return clampBox(snapBox(padBox(viewBox)));
}

// A square is 600 x 450 m on the ground. Zoomed out that is a few pixels,
// less than the width of the route line. Each side that would be shorter than
// minPixels on screen is stretched to it, around the centre of the square.
// Zoomed in, the square keeps its true size.
export function enlargeBox(box, zoom, minPixels) {
    if (!Number.isFinite(zoom)) {
        return { ...box };
    }
    // Web Mercator: the world is 256 * 2^zoom pixels wide for 360 degrees, and
    // a degree of latitude is stretched by 1 / cos(latitude).
    const degreesPerPixel = 360 / (2 ** (zoom + 8));
    const lat = (box.south + box.north) / 2;
    const lng = (box.west + box.east) / 2;
    const stretch = Math.cos((lat * Math.PI) / 180);

    const enlarged = { ...box };
    const widthPx = (box.east - box.west) / degreesPerPixel;
    if (widthPx < minPixels) {
        const half = (minPixels * degreesPerPixel) / 2;
        enlarged.west = lng - half;
        enlarged.east = lng + half;
    }
    const heightPx = (box.north - box.south) / (degreesPerPixel * stretch);
    if (heightPx < minPixels) {
        const half = (minPixels * degreesPerPixel * stretch) / 2;
        enlarged.south = lat - half;
        enlarged.north = lat + half;
    }
    return clampBox(enlarged);
}

// The part two boxes share. Boxes that do not meet give a box that fails
// isValidBox.
export function intersectBox(first, second) {
    if (!first || !second) {
        return null;
    }
    return {
        south: Math.max(first.south, second.south),
        west: Math.max(first.west, second.west),
        north: Math.min(first.north, second.north),
        east: Math.min(first.east, second.east),
    };
}

export function boxContains(outer, inner) {
    if (!outer || !inner) {
        return false;
    }
    return inner.south >= outer.south
        && inner.west >= outer.west
        && inner.north <= outer.north
        && inner.east <= outer.east;
}
